import React, { useState } from 'react';
import styled from 'styled-components';
import { Search, RotateCcw, RefreshCw } from 'lucide-react';
import { CUSTOMER_LIMIT_OPTIONS, CustomerLimitOption } from '../models/CustomerModels';
import {
  colors,
  typography,
  spacing,
  borderRadius,
  shadows,
  zIndex,
} from '../styles/designTokens';

const SearchContainer = styled.div`
  background: ${colors.semantic.background.primary};
  border: 1px solid ${colors.neutral[200]};
  border-radius: ${borderRadius.xl};
  padding: ${spacing[4]};
  margin-bottom: ${spacing[5]};
  box-shadow: ${shadows.sm};
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(
      90deg,
      ${colors.primary[500]} 0%,
      ${colors.primary[700]} 100%
    );
    opacity: 0.8;
  }
`;

const SearchFlex = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[3]};
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
  }
`;

const StatsArea = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
  padding: ${spacing[2]} ${spacing[3]};
  background: linear-gradient(
    135deg,
    ${colors.secondary[50]} 0%,
    ${colors.secondary[200]} 100%
  );
  border-radius: ${borderRadius.lg};
  border: 1px solid ${colors.semantic.border.primary};
  margin-bottom: ${spacing[3]};
  flex-shrink: 0;
`;

const StatsIcon = styled.div`
  width: 20px;
  height: 20px;
  background: linear-gradient(
    135deg,
    ${colors.primary[500]},
    ${colors.primary[700]}
  );
  border-radius: ${borderRadius.sm};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${typography.fontSize.xs};
  color: ${colors.semantic.text.inverse};
`;

const StatsText = styled.div`
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.semibold};
  color: ${colors.semantic.text.secondary};

  .count {
    color: ${colors.primary[700]};
    font-weight: ${typography.fontWeight.bold};
  }
`;

const SearchInputGroup = styled.div`
  flex: 1;
  min-width: 300px;
  position: relative;

  @media (max-width: 768px) {
    min-width: unset;
  }
`;

const FilterGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
  flex-shrink: 0;

  @media (max-width: 768px) {
    justify-content: space-between;
    width: 100%;
  }
`;

const SearchInputContainer = styled.div`
  position: relative;

  .search-icon {
    position: absolute;
    left: 10px;
    top: 50%;
    transform: translateY(-50%);
    color: ${colors.semantic.text.disabled};
    z-index: ${zIndex.docked};
    width: 16px;
    height: 16px;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 10px 10px 10px 36px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${borderRadius.lg};
  font-size: ${typography.fontSize.sm};
  color: ${colors.semantic.text.secondary};
  background: ${colors.semantic.background.primary};
  transition: all 0.2s;
  height: 40px;
  font-family: ${typography.fontFamily.sans};

  &:focus {
    outline: none;
    border-color: ${colors.primary[500]};
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
  }

  &::placeholder {
    color: ${colors.semantic.text.disabled};
    font-size: 13px;
  }
`;

const LimitSelect = styled.select`
  min-width: 100px;
  padding: ${spacing[2]} 10px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${borderRadius.md};
  font-size: 13px;
  color: ${colors.semantic.text.secondary};
  background: ${colors.semantic.background.primary};
  cursor: pointer;
  transition: all 0.2s;
  height: 40px;
  font-family: ${typography.fontFamily.sans};

  &:focus {
    outline: none;
    border-color: ${colors.primary[500]};
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
  }

  @media (max-width: 768px) {
    min-width: 80px;
    flex: 1;
  }
`;

const ActionButton = styled.button<{ variant?: 'primary' | 'secondary' }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: 1px solid;
  border-radius: ${borderRadius.md};
  cursor: pointer;
  transition: all 0.2s;
  flex-shrink: 0;

  ${props =>
    props.variant === 'primary'
      ? `
    background: ${colors.primary[500]};
    border-color: ${colors.primary[500]};
    color: ${colors.semantic.text.inverse};

    &:hover {
      background: ${colors.primary[600]};
      border-color: ${colors.primary[600]};
      transform: translateY(-1px);
    }

    &:active {
      background: ${colors.primary[700]};
      border-color: ${colors.primary[700]};
      transform: translateY(0);
    }
  `
      : `
    background: ${colors.semantic.background.primary};
    border-color: ${colors.neutral[300]};
    color: ${colors.semantic.text.tertiary};

    &:hover {
      background: ${colors.neutral[50]};
      border-color: ${colors.secondary[400]};
      color: ${colors.semantic.text.secondary};
      transform: translateY(-1px);
    }

    &:active {
      background: ${colors.neutral[100]};
      transform: translateY(0);
    }
  `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

interface CustomerSearchBarProps {
  searchQuery: string;
  limit: CustomerLimitOption;
  total: number;
  loading?: boolean;
  onSearch: (query: string) => void;
  onLimitChange: (limit: CustomerLimitOption) => void;
  onRefresh: () => void;
}

const CustomerSearchBar: React.FC<CustomerSearchBarProps> = ({
  searchQuery,
  limit,
  total,
  loading = false,
  onSearch,
  onLimitChange,
  onRefresh,
}) => {
  const [inputValue, setInputValue] = useState<string>(searchQuery);

  const handleSearch = () => {
    onSearch(inputValue);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleReset = () => {
    setInputValue('');
    onSearch('');
  };

  const handleLimitChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newLimit = parseInt(e.target.value) as CustomerLimitOption;
    onLimitChange(newLimit);
  };

  return (
    <SearchContainer>
      <StatsArea>
        <StatsIcon>👥</StatsIcon>
        <StatsText>
          총 <span className="count">{loading ? '...' : total.toLocaleString()}</span>명의 고객
        </StatsText>
      </StatsArea>

      <SearchFlex>
        <SearchInputGroup>
          <SearchInputContainer>
            <Search className="search-icon" />
            <SearchInput
              type="text"
              placeholder="이름, ID, 이메일 등 사용자 등록 번호로 검색..."
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
            />
          </SearchInputContainer>
        </SearchInputGroup>

        <FilterGroup>
          <LimitSelect value={limit} onChange={handleLimitChange}>
            {CUSTOMER_LIMIT_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {option}개
              </option>
            ))}
          </LimitSelect>

          <ActionButton variant="primary" onClick={handleSearch} title="검색">
            <Search />
          </ActionButton>
          <ActionButton variant="secondary" onClick={handleReset} title="초기화">
            <RotateCcw />
          </ActionButton>
          <ActionButton variant="secondary" onClick={onRefresh} title="새로고침">
            <RefreshCw />
          </ActionButton>
        </FilterGroup>
      </SearchFlex>
    </SearchContainer>
  );
};

export default CustomerSearchBar;
